import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { App } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../components/contexts/AuthContext';
import { useSocket } from '../../../components/contexts/SocketContext';
import { inbox, codeOf, sourcingNotice, markRead } from '../models/sourcingApi';
const Context = createContext({ state: 'loading', total: 0, revision: 0, refresh: () => {} });
export const useSourcingMembership = () => useContext(Context);
export function SourcingProvider({ children }) {
  const { authState } = useAuth(), { socket } = useSocket(), { notification } = App.useApp(), navigate = useNavigate();
  const scope = `${authState?.id}:${authState?.token}:${authState?.session_id}:${authState?.isAuthenticated}`;
  const [value, setValue] = useState({ scope: null, state: 'loading', total: 0, revision: 0 });
  const current = useRef(scope), sequence = useRef(0), last = useRef(0);
  current.current = scope;
  const refresh = useCallback(async () => {
    if (!authState?.isAuthenticated) return;
    const ticket = ++sequence.current; last.current = Date.now();
    try {
      const result = await inbox('open', 1, 1);
      if (current.current === scope && ticket === sequence.current) setValue(previous => ({ scope, state: 'member', total: result.total, revision: previous.revision + 1 }));
    } catch (error) {
      if (current.current === scope && ticket === sequence.current) setValue(previous => ({ scope, state: codeOf(error) === 'TEAM_MEMBERSHIP_REQUIRED' ? 'nonmember' : 'error', total: 0, revision: previous.revision + 1 }));
    }
  }, [scope, authState?.isAuthenticated]);
  useEffect(() => { current.current = scope; refresh(); return () => { current.current = null; }; }, [refresh, scope]);
  useEffect(() => {
    const focus = () => { if (Date.now() - last.current >= 300000) refresh(); };
    window.addEventListener('focus', focus);
    if (socket) { socket.on('sourcing_team:changed', refresh); socket.on('connect', refresh); }
    return () => { window.removeEventListener('focus', focus); if (socket) { socket.off('sourcing_team:changed', refresh); socket.off('connect', refresh); } };
  }, [socket, refresh]);
  useEffect(() => {
    if (!socket || !authState?.isAuthenticated) return undefined;
    let alive = true; const seen = new Set();
    const received = event => {
      const row = event?.notification, notice = sourcingNotice(row);
      if (!notice || seen.has(row.id)) return;
      seen.add(row.id); if (seen.size > 200) seen.delete(seen.values().next().value);
      refresh();
      notification.info({ key: notice.key, message: notice.title, description: notice.body, duration: 10, onClick: async () => {
        if (!alive || current.current !== scope) return;
        try { await markRead(row.id); } catch { /* The authorized inbox remains useful if acknowledgement fails. */ }
        if (alive && current.current === scope) { notification.destroy(notice.key); navigate(notice.route); }
      } });
    };
    socket.on('notifications:new', received);
    return () => { alive = false; socket.off('notifications:new', received); seen.forEach(id => notification.destroy(`sourcing:${id}`)); };
  }, [socket, scope, authState?.isAuthenticated, refresh, notification, navigate]);
  return <Context.Provider value={{ ...(value.scope === scope ? value : { state: 'loading', total: 0, revision: 0 }), refresh }}>{children}</Context.Provider>;
}
